import { BasicButton, Loading } from "@/components";
import { BackAppBar, GalleryImage } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import { useBoolean } from "@/hooks/useBoolean";
import useGallery from "@/hooks/useGallery";
import useLocalStorage from "@/hooks/useLocalStorage";
import useMessageMutation from "@/hooks/useMessageMutation";
import usePromptCollection from "@/hooks/usePromptCollection";
import {
	GalleryViewSearchSchema,
	MESSAGE_TAG,
	PostCreateMessageBodySchema,
} from "@/schemas";
import { useGalleryStore } from "@/stores";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import {
	Link,
	Outlet,
	createFileRoute,
	redirect,
	useRouterState,
} from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import upperFirst from "lodash/upperFirst";
import { generateUUID } from "pubnub";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { SelectElement } from "react-hook-form-mui";
import { toast } from "sonner";
import type { z } from "zod";

const checkFormEmpty = (formState: any) =>
	!formState?.watchForm?.tags &&
	!formState?.watchForm?.channel_name &&
	!formState?.watchForm?.text &&
	!formState?.tagValue;

const intialFormGalleryView = {
	watchForm: {
		tags: "",
		channel_name: "",
		text: "",
	},
	tagValue: "",
};

export const Route = createFileRoute("/dashboard/gallery/view")({
	beforeLoad: () => {
		const { images } = useGalleryStore.getState();
		const localFormState = localStorage.getItem("formGalleryView");
		const isLocaFormStateEmpty =
			!isEmpty(localFormState) && localFormState
				? checkFormEmpty(JSON.parse(localFormState))
				: true;
		if (!images.length && isLocaFormStateEmpty) {
			throw redirect({
				to: "/dashboard/camera",
			});
		}
	},
	validateSearch: zodSearchValidator(GalleryViewSearchSchema),
	component: RouteComponent,
});

const FormFieldSchema = PostCreateMessageBodySchema.pick({
	channel_name: true,
	tags: true,
	text: true,
});
type FormFieldSchema = z.infer<typeof FormFieldSchema>;

function RouteComponent() {
	const onBack = useBackward();
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const {
		value: isLoading,
		setTrue: openLoading,
		setFalse: closeLoading,
	} = useBoolean();
	const channelName = useRouterState({
		// @ts-expect-error
		select: (s) => s.location.state?.channelName ?? null,
	});
	const navigate = Route.useNavigate();
	const messageTag = Route.useSearch({
		select: (state) => state.tag,
	});
	const {
		image,
		images,
		isUploadLoading,
		currentImage,
		onViewImage,
		onRemoveImage,
		onResetImages,
		onUploadImages,
	} = useGallery();

	const [formGalleryView, setFormGalleryView] = useLocalStorage(
		"formGalleryView",
		intialFormGalleryView,
	);
	const [tagValue, setTagValue] = useState<string>(
		formGalleryView.tagValue ?? "",
	);
	const { control, watch, getValues } = useForm<FormFieldSchema>({
		mode: "all",
		defaultValues: async () => ({
			tags: !isEmpty(formGalleryView.watchForm.tags)
				? formGalleryView.watchForm.tags
				: messageTag,
			channel_name: channelName,
			text: formGalleryView.watchForm.text ?? "",
		}),
	});

	const watchForm = watch();
	const { tags: currentTag } = watchForm;

	const isEmptyFormGalleryView = useMemo(() => {
		return checkFormEmpty(formGalleryView);
	}, [formGalleryView]);

	const isCaseFiles = useMemo(() => {
		if (!messageTag) return false;
		return [
			MESSAGE_TAG.RESULTS,
			MESSAGE_TAG.IMAGING,
			MESSAGE_TAG.PHOTOS,
			MESSAGE_TAG.FILES,
		].includes(messageTag as any);
	}, [messageTag]);

	const isChatMedia = useMemo(() => {
		if (!messageTag) return false;
		return messageTag === MESSAGE_TAG.MEDIA;
	}, [messageTag]);

	const withAdditionalMessage = isCaseFiles || isChatMedia;

	const onClickRemove = (
		e: React.MouseEvent<HTMLButtonElement>,
		imgIdx: number,
	) => {
		e.stopPropagation();
		e.preventDefault();
		return onRemoveImage(imgIdx);
	};

	const { create } = useMessageMutation();
	const { failedUploadFiles } = usePromptCollection();

	const onClickUpload = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			openLoading();
			return onUploadImages({
				onSuccess: async (res) => {
					localStorage.removeItem("formGalleryView");
					if (res && channelName) {
						const { channel_name, tags, text } = getValues();
						const docTags =
							tags === MESSAGE_TAG.OTHER
								? [MESSAGE_TAG.OTHER.toLowerCase(), tagValue].join("|")
								: tags;
						await create.mutateAsync({
							id: generateUUID(),
							channel_name,
							text,
							documents: res.storages.map((storage, idx) => ({
								file_id: storage.file_id,
								tags: docTags as string,
								ocr_flag: false,
								display_name: images[idx].name,
								filename: images[idx].name,
							})),
						});
						onBack();
					}
					if (!messageTag || !isCaseFiles || !channelName) {
						toast.success("Uploaded!");
						navigate({
							to: "/dashboard/camera",
							replace: true,
						});
					}
					return onResetImages();
				},
				onError: () => {
					return failedUploadFiles();
				},
				onSettled: () => {
					closeLoading();
				},
			});
		},
		[
			channelName,
			isCaseFiles,
			navigate,
			onUploadImages,
			tagValue,
			onBack,
			openLoading,
			closeLoading,
			images,
			getValues,
			create,
			failedUploadFiles,
			onResetImages,
			messageTag,
		],
	);

	const onBeforeBack = useCallback(() => {
		localStorage.removeItem("formGalleryView");
		return onResetImages();
	}, [onResetImages]);

	useEffect(() => {
		if (!images.length && isEmptyFormGalleryView) {
			navigate({
				to: "/dashboard/camera",
				replace: true,
			});
		}
	}, [images, navigate, isEmptyFormGalleryView]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (channelName && !isEmpty(watchForm)) {
			setFormGalleryView({
				watchForm: watchForm as any,
				tagValue,
			});
		}
	}, [channelName, tagValue, watchForm]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.load(); // force reload video when src changes
		}
	}, [image]);

	return (
		<div className="h-full flex flex-col overflow-hidden">
			<BackAppBar beforeBack={onBeforeBack} title="View Files" />
			<div className="relative pb-4 flex-1 flex flex-col gap-4 overflow-y-auto">
				<div className="container">
					<div className="relative aspect-[3/2] bg-dark">
						{image?.type?.startsWith("image") ? (
							<>
								<img
									src={URL.createObjectURL(image)}
									alt={image.name}
									className="w-full h-full object-contain object-center"
								/>
								<Link
									to="/dashboard/gallery/view/$id"
									params={{ id: currentImage }}
									preload={false}
								>
									<IconButton
										type="button"
										color="primary"
										size="large"
										className="!absolute top-2 right-2 z-10 !bg-white"
									>
										<EditIcon fontSize="medium" />
									</IconButton>
								</Link>
							</>
						) : image?.type?.startsWith("video") ? (
							<video
								ref={videoRef}
								controls
								muted={true}
								className="w-full h-full object-contain object-center"
							>
								<source src={URL.createObjectURL(image)} type={image.type} />
								Your browser does not support the video tag.
							</video>
						) : null}
					</div>
				</div>
				<div className="container overflow-x-auto py-4 flex-none">
					<div className="flex flex-row gap-4 [&_div]:w-40 [&_div]:flex-shrink-0 [&_a]:w-40 [&_a]:flex-shrink-0">
						{images.map((image, imageIdx) => (
							<GalleryImage
								key={image.name}
								imgSrc={URL.createObjectURL(image)}
								imgAlt={image.name}
								fileType={image.type}
								isActive={imageIdx === currentImage}
								type={image.type.includes("video") ? "video" : "image"}
								onClickRemove={(e) => onClickRemove(e, imageIdx)}
								onViewImage={() => onViewImage(imageIdx)}
							/>
						))}
						<Link
							to="/dashboard/camera"
							replace={!!messageTag}
							search={{
								action: image?.type?.startsWith("video") ? "video" : "photo",
								tag: messageTag,
							}}
							state={{ channelName }}
							className="relative aspect-[3/2] overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 ease-in-out border-2 border-dashed border-secondary flex justify-center items-center"
						>
							<IconButton
								size="large"
								color="primary"
								className="!bg-secondary"
							>
								<AddIcon className="text-blue-950" />
							</IconButton>
						</Link>
					</div>
				</div>
				{withAdditionalMessage ? (
					<>
						<Divider />
						<div className="container flex flex-col gap-6 pt-4">
							{isCaseFiles ? (
								<>
									<SelectElement
										control={control}
										fullWidth
										label="Label File as"
										name="tags"
										className="[&_.MuiInputBase-root]:!rounded-xl"
										slotProps={{
											inputLabel: {
												shrink: true,
											},
										}}
										options={[
											{
												id: MESSAGE_TAG.RESULTS,
												label: MESSAGE_TAG.RESULTS,
											},
											{
												id: MESSAGE_TAG.IMAGING,
												label: MESSAGE_TAG.IMAGING,
											},
											{
												id: MESSAGE_TAG.PHOTOS,
												label: MESSAGE_TAG.PHOTOS,
											},
											{
												id: MESSAGE_TAG.FILES,
												label: upperFirst(MESSAGE_TAG.FILES),
											},
											{
												id: MESSAGE_TAG.OTHER,
												label: MESSAGE_TAG.OTHER,
											},
										]}
									/>
									{currentTag === MESSAGE_TAG.OTHER ? (
										<TextField
											label="Other"
											className="[&_.MuiInputBase-root]:!rounded-xl"
											value={tagValue}
											onChange={(e) => setTagValue(e.target.value)}
										/>
									) : null}
								</>
							) : null}
							<Controller
								control={control}
								name="text"
								render={({ field }) => (
									<TextField
										multiline
										label="Comment"
										placeholder="Input comment"
										className="[&_.MuiInputBase-root]:!rounded-xl"
										minRows={3}
										inputRef={field.ref}
										slotProps={{
											inputLabel: {
												shrink: true,
											},
										}}
										{...omit(field, ["ref"])}
									/>
								)}
							/>
						</div>
					</>
				) : null}
				<Outlet />
			</div>
			<div className="container py-4 shadow">
				<BasicButton
					type="submit"
					variant="contained"
					color="secondary"
					className="w-full !h-12 text-sm font-semibold !shadow-none"
					onClick={onClickUpload}
					loading={isUploadLoading}
				>
					Upload
				</BasicButton>
			</div>
			<Loading status={isLoading} />
		</div>
	);
}
